import { Icon } from '@iconify/react/dist/iconify.js';
import LoadingSpinner from './LoadingSpinner';
import NavHeader from "./NavHeader";
import axios from "axios";
import { ArcElement, Chart, Legend, Tooltip } from 'chart.js';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../core/AuthContext';
import { URL } from '../core/config';
import "../styles/CompareVolunteers.css";
import "../styles/general.css";
import "../styles/main.css";
import SardineStatsCard from './SardinesStats/SardineStatsCard';
import StatsFilterOptions from './SardinesStats/StatsFilterOptions';
import OpenCallService from '../core/services/OpenCallService';
import { useHistory } from 'react-router-dom';
import CustomAlert from './CustomAlert';
import CustomAlertWithButtons from './CustomAlertWithButtons';
import StackedDepartmentIcons from './Icons';

// Register the required chart components
Chart.register(ArcElement, Tooltip, Legend);

const usersURL = URL + "/users";
const eventsURL = URL + "/events";
const openCallsURL = URL + "/opencall";

const OpenCallVoting = () => {
  const authContext = useAuth();
  const contextUser = authContext.user;

  const location = useLocation();
  const [sardines, setSardines] = useState([]);
  const [allEventsData, setAllEventsData] = useState({});
  const [allOpenCallsData, setAllOpenCallsData] = useState({});
  const [filteredSardines, setFilteredSardines] = useState([]);
  const [openCall, setOpenCall] = useState(null);
  const [sardinesVotes, setSardinesVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventDetails, setEventDetails] = useState(null);
  const [showFull, setShowFull] = useState(false);
  const limit = 200;
  const comment = openCall?.comment || "";
  const isLong = comment.length > limit;
  const displayed = showFull || !isLong ? comment : comment.slice(0, limit) + "...";


  // For CustomAlert
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: '',
    type: '',
    message: '',
    btn1Action: () => { },
    btn2Action: () => { },
    btn1Text: '',
    btn2Text: ''
  });

  const history = useHistory();
  const goBackToOpenCallsList = () =>
    history.push(location.state?.from || '/opencalls-list');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const opencallId = params.get('opencall');

    const getEmptyStats = () => ({
      eventsAsMO: 0,
      eventsAsVMO: 0,
      eventsAsStaff: 0,
      totalEvents: 0,
      ICEEvents: 0,
      STRIPSEvents: 0,
      EDUEvents: 0,
      PARTYEvents: 0,
      PCEvents: 0,
      HREvents: 0,
      OtherEvents: 0,
      shotsWon: 0,
      shotsSent: 0
    });

    const fetchOpenCall = async (openCallId) => {
      try {
        const response = await axios.get(`${openCallsURL}/${openCallId}`);
        const openCall = response.data;
        const sardineIds = openCall.shots.map(shot => shot.user);
        const sardinesVotes = Object.fromEntries(sardineIds.map(id => [id, false]));
        const eventResponse = await axios.get(`${eventsURL}/${openCall.event}`);

        if (sardineIds.length === 0) {
          setLoading(false);
          return;
        }

        const promises = sardineIds.map(id =>
          axios.get(`${usersURL}/${id}`)
            .then(response => response.data)
        );

        const sardinesData = await Promise.all(promises);
        const eventsDataByUser = {};
        const openCallsDataByUser = {};

        const sardinesWithEvents = await Promise.all(sardinesData.map(async sardine => {
          try {
            const eventsResponse = await axios.get(
              `${eventsURL}/eventsbyuser/${sardine._id}`,
            );
            const eventsData = eventsResponse.data;
            eventsDataByUser[sardine._id] = eventsData;

            const openCallsResponse = await axios.get(
              `${openCallsURL}/ocbyuser/${sardine._id}`,
            );
            const openCallsData = openCallsResponse.data;
            const extendedOpenCallsData = []
            for (const openCall of openCallsData) {
              const eventResponse = await axios.get(`${eventsURL}/${openCall.event}`);
              const event = eventResponse.data;
              if (event) {
                extendedOpenCallsData.push({ ...openCall, event });
              }
            }
            openCallsDataByUser[sardine._id] = extendedOpenCallsData;
            // openCallsDataByUser[sardine._id] = openCallsData;
            // console.log("Sardine:", sardine.name, " has this Open Calls:", openCallsData.length);

          } catch (error) {
            console.error(`Error fetching events for ${sardine.name}:`, error);
          } finally {
            // Find the shot for this sardine
            const shot = openCall.shots.find(s => String(s.user) === String(sardine._id));
            return {
              ...sardine,
              stats: getEmptyStats(),
              openCallComment: shot?.comment || "" // Attach the comment here
            };
          }
        }));

        setOpenCall(openCall);
        setAllEventsData(eventsDataByUser);
        setAllOpenCallsData(openCallsDataByUser);
        setSardines(sardinesWithEvents);
        setSardinesVotes(sardinesVotes);
        setFilteredSardines(sardinesWithEvents);
        setEventDetails(eventResponse.data);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching sardines data:', error);
        setLoading(false);
      }
    };

    fetchOpenCall(opencallId);
  }, [location]);

  const handleFilteredSorted = useCallback((filteredSardines) => {
    setFilteredSardines(filteredSardines);
  }, []);

  // For voting
  const [votes, setVotes] = useState(0);

  const handleVote = (sardineId) => {
    const currentVotes = Object.values(sardinesVotes).filter(Boolean).length;
    if (!sardinesVotes[sardineId] && currentVotes === openCall.numberSpots) {
      //alert("Maximum votes reached. Select up to " + openCall.numberSpots + " sardines.");
      setAlertInfo({
        show: true,
        type: 'warning',
        message: "You reached the maximum number of votes. Select up to " + openCall.numberSpots + " sardines. If you want to vote on a different sardine, you need to first unselect some choices.",
      });
      return;
    }
    sardinesVotes[sardineId] = !sardinesVotes[sardineId];
    setVotes(Object.values(sardinesVotes).filter(Boolean).length);
  }

  const handleSubmitVote = async () => {
    const selectedSardines = Object.keys(sardinesVotes).filter((id) => sardinesVotes[id]);
    if (sardines.length >= openCall.numberSpots && selectedSardines.length < openCall.numberSpots) {
      //alert(`Please select more sardines.\nYou have selected ${selectedSardines.length} out of ${openCall.numberSpots} spots.`);

      setAlertInfo({
        show: true,
        type: 'warning',
        message: `Please select more sardines.\nYou have selected ${selectedSardines.length} out of ${openCall.numberSpots} spots.`,
      });

      return;
    }

    try {
      // Attempt to send the shot
      await OpenCallService.voteOC(openCall._id, contextUser._id, selectedSardines);

      // If successful, show success message
      setAlertInfo({
        show: true,
        type: 'success',
        message: 'Your vote was recorded successfully!',
      });

      setTimeout(() => {
        history.push("/opencalls-list", { refresh: true });
        //window.location.href = "/opencall";
      }, 2000);

    } catch (error) {
      // If an error occurs, show error message
      setAlertInfo({
        show: true,
        type: 'error',
        message: 'Failed to record your vote. Please try again.',
      });
      console.log(error.message);
    }

    //OpenCallService.voteOC(openCall._id, contextUser._id, selectedSardines);
    //history.push("/opencalls-list", { refresh: true });
  }

  const handleDiscard = () => {

    setAlertActionInfo({
      show: true,
      title: 'Do you want to leave without voting?',
      type: 'error',
      message: 'Are you sure you want to leave this page without casting your vote? Your choices won\'t be counted.',
      btn1Action: () => {
        // Just close the alert without navigating
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn2Action: () => {
        // Navigate to the events list page if confirmed
        goBackToOpenCallsList();
        // Close the alert after navigation
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn1Text: 'No, nevermind',
      btn2Text: 'Yes, let\'s leave',
    });

  }

  // -----------------------------

  if (loading) {
    return (
      <div className="page-container">
        <LoadingSpinner message="Loading sardines data" />
      </div>
    );
  }

  if (sardines.length === 0) {
    return (
      <div className="page-container">
        <h1 className='page-title'>There are no sardines for comparison</h1>
        <p style={{ textAlign: 'center' }}>Please go back and select sardines to compare.</p>
      </div>
    );
  }

  // Added to check if the voter has already voted
  if (openCall != null && openCall.voters.includes(contextUser._id)) {
    return <div style={{width:"90%",maxWidth:"450px",display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", textAlign:"center"}}>
      <Icon icon="mingcute:alert-fill" width="48" height="48" />
      <p style={{marginTop:"30px"}}><b>You have already submitted your vote for this Open Call.</b> There's nothing for you to do here now. You can go.</p>
      <Link to={"/open-call"} className="button button-transparent-black" style={{ width: "fit-content" }}>Back to Open Calls</Link>
    </div>
  }

  return (
    <div className="page-container">
      <NavHeader />

      <h1 className='page-title'>Sardines Comparison</h1>

      <div className={`vote-instructions ${votes >= openCall.numberSpots ? "votes-complete" : ""}`}>
        Current votes: {votes} / {openCall.numberSpots}
      </div>

      {eventDetails && (
        <div className="event-summary-box">

          <h2 className="event-summary-title">
            <span className="event-title-icon">
              {eventDetails.departments?.[0] && <StackedDepartmentIcons departments={eventDetails.departments} />}
            </span>
            {eventDetails.name}
          </h2>

          {comment && (
            <p className="event-detail-line">
              <Icon icon="mdi:text-box-outline" className="event-detail-icon" />
              {displayed}
              {isLong && (
                <span
                  className="show-more-text"
                  onClick={() => setShowFull(!showFull)}
                >
                  {showFull ? "Show less" : "Show more"}
                </span>
              )}
            </p>
          )}

          <p className="event-detail-line">
            <Icon icon="mdi:calendar-outline" className="event-detail-icon" />
            {new Date(eventDetails.date).toLocaleDateString()}
          </p>

          <p className="event-detail-line">
            <Icon icon="mdi:map-marker" className="event-detail-icon" />
            {eventDetails.meetingPoint}
          </p>

        </div>
      )}

      <StatsFilterOptions
        sardines={sardines}
        allEventsData={allEventsData}
        allOpenCallsData={allOpenCallsData}
        onFilteredSorted={handleFilteredSorted}
      />

      <div className="section">
        <div className="comparison-container">
          {filteredSardines.map((sardine) => {
            return <SardineStatsCard key={sardine._id} sardine={sardine} onVote={handleVote} isVoted={sardinesVotes[sardine._id]} />
          })}
        </div>
      </div>

      <div className="button-container">
        <button
          className="button back-button"
          onClick={handleDiscard}
          style={{ width: 'fit-content' }}
        >
          Discard selection
        </button>
        <button className="button submit-vote-button" onClick={handleSubmitVote}>
          Submit Vote
        </button>
      </div>
      {/* Alert that shows upon event submission */}
      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss={true}
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
        />
      )}

      {alertActionInfo.show && (
        <CustomAlertWithButtons
          title={alertActionInfo.title}
          type={alertActionInfo.type}
          message={alertActionInfo.message}
          onButton1Click={alertActionInfo.btn1Action}
          onButton2Click={alertActionInfo.btn2Action}
          button1Text={alertActionInfo.btn1Text}
          button2Text={alertActionInfo.btn2Text}
        />)}
    </div>
  );
};

export default OpenCallVoting;
